import { ArrowRight, CalendarDays, Clock, Monitor, Users } from "lucide-react";
import { Section, SectionHeading } from "@/components/ui";
import { getProgramCardImage, getProgramDetail, getProgramSourceUrl } from "@/data/programDetails";

/** Two-up on tablet and desktop so every card has room for the same facts. Full literals for Tailwind. */
const GRID = {
  1: "max-w-xl",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-2 lg:grid-cols-3",
  4: "sm:grid-cols-2",
};

/**
 * Programme cards on every industry page. Layout is identical; facts come from the live
 * A Learning Lab programme pages (duration, ages, sessions, summary) via programDetails.
 */
export function IndustryProgrammes({ programmes }) {
  const cards = programmes.cards;

  return (
    <Section id="programmes" tone="sunTint" labelledBy="industry-programmes-title" className="scroll-mt-24" testId="industry-programs">
      <SectionHeading id="industry-programmes-title" title={programmes.title} intro={programmes.lead} />

      <ul className={`mt-8 grid gap-4 sm:mt-10 sm:gap-6 ${GRID[cards.length] ?? GRID[4]}`} data-testid="industry-programme-cards">
        {cards.map((card) => {
          const program = getProgramDetail(card.slug);
          const image = getProgramCardImage(card.slug) || card.image || null;
          const summary = program?.summary || card.text;
          const duration = program?.duration || card.duration;
          const ages = program?.ages || card.ages;
          const sessions = program?.sessions || card.sessions;
          const href = card.href || getProgramSourceUrl(card.slug);
          const linkClass =
            "mt-auto inline-flex min-h-[44px] items-center gap-2 pt-5 font-bold text-brand-600 after:absolute after:inset-0 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink";

          return (
            <li key={card.slug}>
              <article
                className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-brand-200 bg-white"
                data-testid={`industry-programme-${card.slug}`}
              >
                {image ? (
                  <div className="relative aspect-[16/10] shrink-0 overflow-hidden bg-brand-50">
                    <img
                      src={image}
                      alt=""
                      width={800}
                      height={500}
                      loading="lazy"
                      decoding="async"
                      onError={(event) => {
                        event.currentTarget.style.visibility = "hidden";
                      }}
                      className="h-full w-full object-cover"
                    />
                    <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-ink">
                      <Monitor className="h-3.5 w-3.5" aria-hidden="true" />
                      Live online
                    </span>
                  </div>
                ) : null}

                <div className="flex flex-1 flex-col p-5 sm:p-6">
                  <h3 className="font-heading text-xl font-bold leading-tight sm:text-2xl">{card.title}</h3>
                  {summary ? <p className="mt-3 text-sm leading-relaxed text-ink/70 sm:text-base">{summary}</p> : null}

                  <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs font-semibold text-ink/70 sm:text-sm">
                    {duration ? (
                      <li className="inline-flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" />
                        {duration}
                      </li>
                    ) : null}
                    {ages ? (
                      <li className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" />
                        Ages {ages}
                      </li>
                    ) : null}
                    {sessions ? (
                      <li className="inline-flex items-center gap-1.5">
                        <CalendarDays className="h-3.5 w-3.5 shrink-0 text-brand-600" aria-hidden="true" />
                        {sessions} live sessions
                      </li>
                    ) : null}
                  </ul>

                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" className={linkClass}>
                      {programmes.link}
                      <ArrowRight className="h-4 w-4" aria-hidden="true" />
                      <span className="sr-only"> {card.title}</span>
                    </a>
                  ) : null}
                </div>
              </article>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
